import { isBrowser } from '@/Module/Core/Helper';
import type { ILessonSection } from '@/Module/Lesson/Model/DataModel';

const VIDEO_DURATION_TIME = 30;

class VideoLoginService {
  private data: Map<ILessonSection['id'], ILessonSection['duration']> = new Map();

  private key = 'videoTime';

  constructor(key = 'videoTime') {
    this.key = key;
    this.reloadLocalStorage();
  }

  public reloadLocalStorage(): Promise<typeof this.data> {
    return new Promise(resolve => {
      if (!isBrowser()) return;

      const json: Record<ILessonSection['id'], ILessonSection['duration']> = JSON.parse(
        localStorage.getItem(this.key) || '{}',
      );
      Object.entries(json).forEach(item => {
        this.data.set(Number(item[0]), item[1]);
      });

      resolve(this.data);
    });
  }

  private updateLocalStorage() {
    localStorage.setItem(this.key, JSON.stringify(Object.fromEntries(this.data.entries()) ?? '{}'));
  }

  public getVideoDuration(lessonId: ILessonSection['id']) {
    return this.data.get(lessonId) ?? 0;
  }

  public setVideoDuration(lessonId: ILessonSection['id'], duration: ILessonSection['duration']) {
    this.data.set(lessonId, duration);
    this.updateLocalStorage();
  }

  public increaseVideoDuration(lessonId: ILessonSection['id']) {
    this.setVideoDuration(lessonId, this.getVideoDuration(lessonId) + 1);
  }

  public clearVideoDuration(lessonId: ILessonSection['id']) {
    this.data.delete(lessonId);
    this.updateLocalStorage();
  }

  public clear() {
    this.data.clear();
    this.updateLocalStorage();
  }

  public canContinueWatching(lessonId: ILessonSection['id']) {
    return this.getVideoDuration(lessonId) < VIDEO_DURATION_TIME;
  }
}

export default VideoLoginService;
